/**
 * Rapor dışa aktarma: JSON (makine okunur), HTML (arşiv/rapor), CSV (bulgu listesi).
 *
 * Tümü tarayıcıda üretilir; ağ yok. Metinler seçili arayüz diline göre üretilir.
 */

import type { Issue, IssueCategory, Report } from '../types';
import { i18n, t } from '../i18n/state.svelte';
import { issueTextEn } from '../i18n/issues';
import { categoryLabel, confidenceShort, locale, severityLabel } from '../ui/labels';

function issueText(issue: Issue): { title: string; detail: string; suggestion?: string } {
	const en = i18n.lang === 'en' ? issueTextEn(issue) : null;
	return {
		title: en?.title ?? issue.title,
		detail: en?.detail ?? issue.detail,
		suggestion: en?.suggestion ?? issue.suggestion
	};
}

export function toJson(report: Report): string {
	return JSON.stringify(report, null, 2);
}

export function toCsv(report: Report): string {
	const rows: string[][] = [
		[
			t('export.csv.category'),
			t('export.csv.severity'),
			t('export.csv.code'),
			t('export.csv.title'),
			t('export.csv.detail'),
			t('export.csv.confidence'),
			t('export.csv.decisive'),
			t('export.csv.page'),
			t('export.csv.line'),
			t('export.csv.quote')
		]
	];
	for (const issue of report.issues) {
		const text = issueText(issue);
		rows.push([
			categoryLabel(issue.category),
			severityLabel(issue.severity),
			issue.code,
			text.title,
			text.detail,
			confidenceShort(issue.confidence),
			issue.decisive ? t('common.yes') : t('common.no'),
			issue.location.page !== undefined ? String(issue.location.page) : '',
			issue.location.sourceLine !== undefined ? String(issue.location.sourceLine) : '',
			issue.quote ?? ''
		]);
	}
	return rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
}

function csvCell(v: string): string {
	const s = v.replace(/\r?\n/g, ' ');
	return /[";,]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toHtml(report: Report): string {
	const esc = escapeHtml;
	const date = new Date(report.generatedAt).toLocaleString(locale());
	const cats = report.scores.categories
		.map(
			(c) => `<tr>
			<td>${esc(categoryLabel(c.category))}</td>
			<td class="num">${c.score}</td>
			<td class="num">%${Math.round(c.weight * 100)}</td>
			<td>${c.note ? esc(c.note) : esc(t('export.findingsCount', { n: c.issueCount }))}</td>
		</tr>`
		)
		.join('\n');

	const byCategory = new Map<IssueCategory, Issue[]>();
	for (const issue of report.issues) {
		const list = byCategory.get(issue.category) ?? [];
		list.push(issue);
		byCategory.set(issue.category, list);
	}

	const sections = [...byCategory.entries()]
		.map(([cat, issues]) => {
			const rows = issues
				.map((i) => {
					const text = issueText(i);
					return `<li class="issue sev-${i.severity}">
					<div class="issue-head">
						<span class="badge">${esc(severityLabel(i.severity))}</span>
						<strong>${esc(text.title)}</strong>
						<span class="conf">${esc(t('export.confidence', { level: confidenceShort(i.confidence) }))}${
							i.decisive ? '' : ` · ${esc(t('issue.notDecisive'))}`
						}</span>
					</div>
					<p>${esc(text.detail)}</p>
					${i.quote ? `<blockquote>${esc(i.quote)}</blockquote>` : ''}
					${text.suggestion ? `<p class="suggestion"><em>${esc(t('issue.suggestion'))}:</em> ${esc(text.suggestion)}</p>` : ''}
					${i.location.page !== undefined ? `<p class="loc">${esc(t('common.page', { n: i.location.page }))}</p>` : ''}
				</li>`;
				})
				.join('\n');
			return `<section><h2>${esc(categoryLabel(cat))}</h2><ul>${rows}</ul></section>`;
		})
		.join('\n');

	return `<!doctype html>
<html lang="${i18n.lang}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(t('export.title'))} — ${esc(report.document.fileName)}</title>
<style>
	:root { color-scheme: light; }
	body { font: 15px/1.6 -apple-system, "Segoe UI", Roboto, sans-serif; color: #1c1917; background: #faf9f6; margin: 0; padding: 40px 24px; }
	main { max-width: 880px; margin: 0 auto; }
	h1 { font-size: 26px; margin: 0 0 4px; }
	.meta { color: #57534e; font-size: 13px; margin-bottom: 28px; }
	.score { display: flex; align-items: baseline; gap: 14px; background: #fff; border: 1px solid #e7e5e4; border-radius: 14px; padding: 20px 24px; margin-bottom: 24px; }
	.score b { font-size: 44px; line-height: 1; }
	.score .grade { font-size: 20px; color: #0f766e; font-weight: 700; }
	table { width: 100%; border-collapse: collapse; background: #fff; border: 1px solid #e7e5e4; border-radius: 14px; overflow: hidden; margin-bottom: 32px; }
	th, td { text-align: left; padding: 10px 14px; border-bottom: 1px solid #f1efec; font-size: 14px; }
	th { background: #f5f3ef; font-weight: 600; }
	td.num { font-variant-numeric: tabular-nums; }
	section h2 { font-size: 18px; margin: 28px 0 10px; }
	ul { list-style: none; padding: 0; margin: 0; display: grid; gap: 12px; }
	li.issue { background: #fff; border: 1px solid #e7e5e4; border-left: 4px solid #a8a29e; border-radius: 10px; padding: 14px 16px; }
	li.sev-critical { border-left-color: #b91c1c; }
	li.sev-warning { border-left-color: #b45309; }
	li.sev-info { border-left-color: #0369a1; }
	.issue-head { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 6px; }
	.badge { font-size: 11px; text-transform: uppercase; letter-spacing: .04em; background: #f5f3ef; padding: 2px 8px; border-radius: 99px; }
	.conf { font-size: 12px; color: #78716c; margin-left: auto; }
	p { margin: 6px 0; }
	blockquote { margin: 8px 0; padding: 8px 12px; background: #faf9f6; border-left: 3px solid #d6d3d1; font-size: 13px; color: #44403c; }
	.suggestion { color: #44403c; font-size: 13px; }
	.loc { font-size: 12px; color: #78716c; }
	footer { margin-top: 40px; font-size: 12px; color: #78716c; }
</style>
</head>
<body>
<main>
	<h1>${esc(t('export.title'))}</h1>
	<p class="meta">${esc(report.document.fileName)} · ${esc(report.document.format.toUpperCase())} · ${date}</p>
	<div class="score">
		<b>${report.scores.overall}</b>
		<span>/100</span>
		<span class="grade">${report.scores.grade}</span>
		<span class="conf">${esc(
			t('export.summary', {
				critical: report.counts.critical,
				warning: report.counts.warning,
				undecided: report.undecided
			})
		)}</span>
	</div>
	<table>
		<thead><tr><th>${esc(t('export.colCategory'))}</th><th>${esc(t('export.colScore'))}</th><th>${esc(t('export.colWeight'))}</th><th>${esc(t('export.colNote'))}</th></tr></thead>
		<tbody>${cats}</tbody>
	</table>
	${sections}
	<footer>${esc(t('export.footer'))}</footer>
</main>
</body>
</html>`;
}

function escapeHtml(s: string): string {
	return s
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

/** Tarayıcıda dosya indirir. */
export function downloadFile(content: string, fileName: string, mime: string): void {
	const blob = new Blob([content], { type: `${mime};charset=utf-8` });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 2000);
}
